import { MetricField, SliderField } from "@/components/dashboard/metrics-games/common";
import { OpportunityScoreStartupSelector } from "@/components/dashboard/metrics-games/opportunity-score-startup-selector";
import { SCORE_WEIGHTS, confidenceMultiplier } from "@/components/dashboard/metrics-games/utils";
import type { ScoreGameInputs } from "@/components/dashboard/metrics-games/types";

type Props = {
  scoreInputs: ScoreGameInputs;
  onUpdateScoreInput: (key: keyof ScoreGameInputs, value: number) => void;
  searchInCard?: boolean;
};

export function GameOpportunityScore({ scoreInputs, onUpdateScoreInput, searchInCard }: Props) {
  const normalizedMedian = scoreInputs.medianMrr / 100;
  const normalizedGrowth = scoreInputs.averageGrowth30d / 100;
  const normalizedTotalMrr = scoreInputs.totalMrr / 100;
  const normalizedCompetition = scoreInputs.startupCount / 100;
  const normalizedForSale = scoreInputs.forSaleRatio / 100;
  const scoreConfidenceMultiplier = confidenceMultiplier(scoreInputs.startupCountForConfidence);

  const weightedMedian = normalizedMedian * SCORE_WEIGHTS.medianMrr;
  const weightedGrowth = normalizedGrowth * SCORE_WEIGHTS.averageGrowth30d;
  const weightedTotalMrr = normalizedTotalMrr * SCORE_WEIGHTS.totalMrr;
  const weightedCompetition = normalizedCompetition * SCORE_WEIGHTS.startupCount;
  const weightedForSale = normalizedForSale * SCORE_WEIGHTS.forSaleRatio;
  const rawScore = weightedMedian + weightedGrowth + weightedTotalMrr + weightedCompetition + weightedForSale;
  const finalOpportunityScore = Math.round(rawScore * 100 * scoreConfidenceMultiplier);

  return (
    <article className="card metrics-game">
      <div className="metrics-game-head">
        <p className="section-label">Game 3</p>
        <h2 className="card-title">Opportunity score formula</h2>
      </div>
      <p className="card-desc">
        These sliders represent normalized factor scores (0-100). The formula computes raw score then applies confidence.
      </p>

      {searchInCard && (
        <div className="metrics-game-tenk-search">
          <OpportunityScoreStartupSelector slot="both" />
        </div>
      )}

      <div className="metrics-slider-grid">
        <SliderField
          label="Median MRR score"
          value={scoreInputs.medianMrr}
          onChange={(value) => onUpdateScoreInput("medianMrr", value)}
        />
        <SliderField
          label="Growth score"
          value={scoreInputs.averageGrowth30d}
          onChange={(value) => onUpdateScoreInput("averageGrowth30d", value)}
        />
        <SliderField
          label="Total MRR score"
          value={scoreInputs.totalMrr}
          onChange={(value) => onUpdateScoreInput("totalMrr", value)}
        />
        <SliderField
          label="Competition advantage score"
          value={scoreInputs.startupCount}
          onChange={(value) => onUpdateScoreInput("startupCount", value)}
        />
        <SliderField
          label="For-sale ratio score"
          value={scoreInputs.forSaleRatio}
          onChange={(value) => onUpdateScoreInput("forSaleRatio", value)}
        />
        <div className="metrics-slider-row">
          <label className="form-label" htmlFor="score-sample-size">
            Sample size for confidence: {scoreInputs.startupCountForConfidence}
          </label>
          <div
            style={{
              "--slider-fill": `${((scoreInputs.startupCountForConfidence - 1) / 29) * 100}%`,
            } as React.CSSProperties}
          >
            <input
              id="score-sample-size"
              type="range"
              min={1}
              max={30}
              className="metrics-slider"
              value={scoreInputs.startupCountForConfidence}
              onChange={(event) => onUpdateScoreInput("startupCountForConfidence", Number(event.target.value))}
            />
          </div>
        </div>
      </div>

      <div className="score-breakdown-grid">
        <MetricField label="Weighted median piece" value={weightedMedian.toFixed(3)} />
        <MetricField label="Weighted growth piece" value={weightedGrowth.toFixed(3)} />
        <MetricField label="Weighted total MRR piece" value={weightedTotalMrr.toFixed(3)} />
        <MetricField label="Weighted crowding piece" value={weightedCompetition.toFixed(3)} />
        <MetricField label="Weighted for-sale piece" value={weightedForSale.toFixed(3)} />
        <MetricField label="Raw score" value={(rawScore * 100).toFixed(1)} />
        <MetricField label="Confidence multiplier" value={scoreConfidenceMultiplier.toFixed(2)} />
        <MetricField label="Final opportunity score" value={String(finalOpportunityScore)} />
      </div>

      <p className="metrics-formula">
        <code>
          score = 0.35*median + 0.20*growth + 0.20*total + 0.15*competition + 0.10*forSale
        </code>
        <code> final = round(score * 100 * confidenceMultiplier)</code>
      </p>
    </article>
  );
}
